import React, { createContext, useState, useContext, useCallback, useMemo, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useQuery } from 'react-query';
import { styled } from '@linaria/react';
import { useFirebase } from 'components/Firebase/index.js';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { ONE_TO_MANY_PERMISSIONS } from 'shared-permissions';
import { isSecureContext, isWebRTCSupported } from '@bambuser/bambuser-broadcaster-js/lib/utils';
import BambuserBroadcaster from '@bambuser/bambuser-broadcaster-js/lib/BambuserBroadcaster';
import { Modal, Button } from '@livecommerce/design-system';
import BaasBroadcaster from 'components-v3/BaasBroadcaster/index.jsx';
import useWebBroadcasterCredentials from 'hooks/useWebBroadcasterCredentials.js';
import usePermissions from 'hooks/usePermissions.jsx';
import { addSnackbarNotification } from 'actions/notifications.js';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import { useModal, useOrgId, useOrganizations } from 'hooks';
import { getOrgIdFromPathname } from 'hooks/useOrgId.js';
import useArchiveEvent from 'hooks/useArchiveEvent.js';
import { ASPECT_RATIO_TYPES } from 'constants/image.js';

const { PORTRAIT, LANDSCAPE } = ASPECT_RATIO_TYPES;

const RESOLUTIONS = {
  [PORTRAIT]: { width: 405, height: 720 },
  [LANDSCAPE]: { width: 1280, height: 720 },
};

const CLOSE_REASONS = {
  NAVIGATION: 'navigation',
  MANUAL: 'manual',
};

const VideoSurfaceContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BroadcasterContext = createContext({
  isOpen: false,
  isLoading: false,
  isSupported: false,
  isDisabled: false,
  openBroadcaster: () => {},
  closeBroadcaster: () => {},
  setPortalTarget: () => {},
  broadcasterProps: {},
  broadcasterState: {},
});

export const BroadcasterProvider = ({ children }) => {
  const { t } = useLocalization();
  const firebase = useFirebase();
  const dispatch = useDispatch();
  const history = useHistory();
  const { features: { webCameraEnabled } } = useFeatureContext();
  const { hasPermission } = usePermissions();
  const hasShowPermission = hasPermission(ONE_TO_MANY_PERMISSIONS.SHOWS);
  const addSnackbar = useCallback((payload) => dispatch(addSnackbarNotification(payload)), [dispatch]);
  const prevBroadcastingRef = useRef(false);
  const isDeviceSwitchingRef = useRef(false);
  const orgId = useOrgId();
  const { data: organizations = [] } = useOrganizations();
  const currentOrganization = organizations.find(({ id }) => id === orgId);
  const [closeReason, setCloseReason] = useState(CLOSE_REASONS.MANUAL);
  const blockedNavigationRef = useRef();
  const [state, setState] = useState({
    isOpen: false,
    broadcasterProps: {},
    broadcasterState: {},
  });

  const { mutate: archiveEvent, error: archiveError } = useArchiveEvent(state?.broadcasterProps?.eventId);
  const {
    open: openCloseDialog,
    close: closeCloseDialog,
    isOpen: isCloseDialogOpen,
  } = useModal();

  const { data: media, isLoading: settingsLoading, isError: mediaError } = useQuery({
    queryKey: ['orgSettings', orgId, 'media'],
    queryFn: async () => {
      const agent = await firebase.getAgent();
      const response = await agent.get(`/o/${orgId}/settings/media`);
      return response.body;
    },
    enabled: !!orgId && state.isOpen && webCameraEnabled && hasShowPermission,
  });

  // Portal target — null means document.body (default)
  const [portalTarget, setPortalTarget] = useState(null);

  const [aspectRatio, setAspectRatioState] = useState(PORTRAIT);

  // BambuserBroadcaster instance managed directly in context
  const [broadcasterInstance, setBroadcasterInstance] = useState(null);

  const videoElRef = useRef(null);
  const videoSurfaceContainerRef = useRef(null);

  // Ref callback passed to BaasBroadcaster — injects the video element into whichever
  // container div is currently mounted, even after portal target changes
  const handleVideoSurfaceRef = useCallback((node) => {
    videoSurfaceContainerRef.current = node;
    if (node && videoElRef.current && !node.contains(videoElRef.current)) {
      node.appendChild(videoElRef.current);
      // Moving a video element in the DOM can cause it to pause or go black (especially in Safari).
      // Re-assigning the srcObject and calling play() ensures it resumes rendering.
      const srcObject = videoElRef.current.srcObject;
      if (srcObject) {
        videoElRef.current.srcObject = srcObject;
      }
      videoElRef.current.play().catch(() => {});
    }
  }, []);

  // Create the video element once per session (tied to isOpen, not selectedDeviceId).
  // The same DOM node is reused across broadcaster recreations so _onSetupMediaSuccess
  // always updates the visible element — matching BambuserBroadcasterContainer behaviour.
  useEffect(() => {
    if (!state.isOpen) return;
    const videoEl = document.createElement('video');
    videoEl.autoplay = true;
    videoEl.muted = true;
    videoEl.setAttribute('playsinline', '');
    videoElRef.current = videoEl;
    if (videoSurfaceContainerRef.current) {
      videoSurfaceContainerRef.current.appendChild(videoEl);
    }
    return () => {
      if (videoEl.parentNode) videoEl.parentNode.removeChild(videoEl);
      videoElRef.current = null;
    };
  }, [state.isOpen]);

  const broadcasterVideoSurface = useMemo(() => <VideoSurfaceContainer ref={handleVideoSurfaceRef} /> ,[handleVideoSurfaceRef]);

  const effectiveAppId = state.broadcasterProps?.appId || media?.applicationId;

  const { data: broadcasterCredentials, isLoading, isError } = useWebBroadcasterCredentials(
    effectiveAppId,
    { enabled: state.isOpen && webCameraEnabled && hasShowPermission },
  );

  useEffect(() => {
    if (!isError && !mediaError) return;
    addSnackbar({
      title: t('broadcaster.errorTitle'),
      text: t('broadcaster.credentialsErrorMessage'),
      type: 'error',
    });
    closeBroadcaster();
  }, [isError, mediaError, t, addSnackbar]);

  useEffect(() => {
    if (!archiveError) return;
    addSnackbar({
      title: t('broadcaster.errorTitle'),
      text: archiveError?.message || t('show.archiveErrorMessage'),
      type: 'error',
    });
  }, [archiveError, t, addSnackbar]);

  useEffect(() => {
    const isBroadcasting = state.broadcasterState?.broadcasting;
    const preventUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    const unblock = history.block((location) => {
      if (!isBroadcasting) return undefined;
      const nextOrgId = getOrgIdFromPathname(location.pathname);
      if (orgId && nextOrgId === orgId) return undefined;
      if (!isCloseDialogOpen) {
        openCloseDialog();
        setCloseReason(CLOSE_REASONS.NAVIGATION);
      }
      blockedNavigationRef.current = location.pathname;
      return false;
    });
    if (isBroadcasting) {
      window.addEventListener('beforeunload', preventUnload);
    } else {
      window.removeEventListener('beforeunload', preventUnload);
    }
    return () => {
      window.removeEventListener('beforeunload', preventUnload);
      return unblock();
    };
  }, [state.broadcasterState?.broadcasting, orgId, history, isCloseDialogOpen, openCloseDialog]);

  const openBroadcaster = useCallback((props) => {
    setState((prevState) => ({
      ...prevState,
      isOpen: true,
      broadcasterProps: props,
    }));
  }, []);

  const switchVideoDevice = useCallback((deviceId) => {
    if (!broadcasterInstance) return;
    isDeviceSwitchingRef.current = true;
    broadcasterInstance.videoInputDevice = deviceId;
  }, [broadcasterInstance]);

  const switchAudioDevice = useCallback((deviceId) => {
    if (!broadcasterInstance) return;
    isDeviceSwitchingRef.current = true;
    broadcasterInstance.audioInputDevice = deviceId;
  }, [broadcasterInstance]);

  const setResolution = useCallback((ratio) => {
    const { width, height } = RESOLUTIONS[ratio];
    broadcasterInstance?.setResolution(width, height);
    setAspectRatioState(ratio);
  }, [broadcasterInstance]);

  const close = useCallback(() => {
    closeCloseDialog();
    setState((prevState) => ({
      ...prevState,
      broadcasterState: {},
      isOpen: false,
    }));
    setCloseReason(CLOSE_REASONS.MANUAL);
    setAspectRatioState(PORTRAIT);
  }, [closeCloseDialog]);

  const closeBroadcaster = useCallback((e) => {
    e?.stopPropagation();
    if (state.broadcasterState?.broadcasting) {
      openCloseDialog();
      setCloseReason(CLOSE_REASONS.MANUAL);
    } else {
      close();
    }
  }, [state.broadcasterState?.broadcasting, openCloseDialog, close]);

  const handleError = useCallback((error) => {
    addSnackbar({
      title: t('broadcaster.errorTitle'),
      text: error?.message || t('broadcaster.errorMessage'),
      type: 'error',
    });
  }, [t, addSnackbar]);

  // Keep a ref so the broadcaster useEffect never captures a stale handleError
  const handleErrorRef = useRef(handleError);
  handleErrorRef.current = handleError;

  useEffect(() => {
    const isBroadcasting = state.broadcasterState?.broadcasting;
    if (prevBroadcastingRef.current && !isBroadcasting && state.broadcasterProps?.eventId) {
      archiveEvent({ id: state.broadcasterProps.eventId });
      const targetUrl = blockedNavigationRef.current;
      if (targetUrl) {
        blockedNavigationRef.current = null;
        history.push(targetUrl);
      }
      close();
    }
    prevBroadcastingRef.current = isBroadcasting;
  }, [state.broadcasterState?.broadcasting, state.broadcasterProps?.eventId, archiveEvent, close, history]);

  const handleCancelClose = useCallback(() => {
    closeCloseDialog();
    setCloseReason(CLOSE_REASONS.MANUAL);
    blockedNavigationRef.current = null;
  }, [closeCloseDialog]);

  // Create and manage the BambuserBroadcaster instance directly.
  // Keeping it in the context decouples the broadcaster lifecycle from the UI render location,
  // so the portal target can change without interrupting an active broadcast.
  useEffect(() => {
    if (!state.isOpen || isLoading || !broadcasterCredentials || !state.broadcasterProps || !videoElRef.current) return;

    const { eventId, video = true, audio = true, archive = true } = state.broadcasterProps;
    const videoEl = videoElRef.current;

    const broadcaster = new BambuserBroadcaster({
      video,
      audio,
      archive,
      width: RESOLUTIONS[PORTRAIT].width,
      height: RESOLUTIONS[PORTRAIT].height,
      allowGracefulConstraintDegradation: false,
      customData: JSON.stringify({ originalEventId: eventId }),
      ...broadcasterCredentials,
      ...(video ? { videoSurface: videoEl } : {}),
    });

    setBroadcasterInstance(broadcaster);

    const onStart = () => setState(prev => ({ ...prev, broadcasterState: { ...prev.broadcasterState, broadcasting: true } }));
    const onStop = () => {
      if (isDeviceSwitchingRef.current) return;
      setState(prev => ({ ...prev, broadcasterState: { ...prev.broadcasterState, broadcasting: false } }));
    };
    const onMuteChange = (muted) => setState(prev => ({ ...prev, broadcasterState: { ...prev.broadcasterState, muted } }));
    const onCameraChange = (cameraOn) => setState(prev => ({ ...prev, broadcasterState: { ...prev.broadcasterState, cameraOn } }));
    const onInputAccess = () => {
      isDeviceSwitchingRef.current = false;
      setState(prev => ({
        ...prev,
        broadcasterState: {
          ...prev.broadcasterState,
          inputAccess: true,
          muted: broadcaster.muted,
          cameraOn: broadcaster.cameraOn,
        },
      }));
    };
    const onBroadcastRegistered = () => setState(prev => ({ ...prev, broadcasterState: { ...prev.broadcasterState, registered: true } }));
    const onError = (error) => handleErrorRef.current(error);

    broadcaster
      .on('start', onStart)
      .on('stop', onStop)
      .on('muteChange', onMuteChange)
      .on('cameraChange', onCameraChange)
      .on('inputAccess', onInputAccess)
      .on('broadcastRegistered', onBroadcastRegistered)
      .on('error', onError);

    let canceled = false;
    broadcaster._setupMediaDevices().catch((reason) => {
      if (canceled) return;
      handleErrorRef.current(reason);
    });
    return () => {
      canceled = true;
      broadcaster
        .removeListener('start', onStart)
        .removeListener('stop', onStop)
        .removeListener('muteChange', onMuteChange)
        .removeListener('cameraChange', onCameraChange)
        .removeListener('inputAccess', onInputAccess)
        .removeListener('broadcastRegistered', onBroadcastRegistered)
        .removeListener('error', onError);
      broadcaster.stop();
      setBroadcasterInstance(null);
    };
  }, [state.isOpen, isLoading, broadcasterCredentials, state.broadcasterProps]);

  const value = useMemo(
    () => ({
      ...state,
      isLoading: isLoading || settingsLoading,
      isSupported: isSecureContext() && isWebRTCSupported(),
      isDisabled: Object.entries(currentOrganization?.suspensions || {}).some(([,v]) => v.status),
      openBroadcaster,
      closeBroadcaster,
      setPortalTarget,
      switchVideoDevice,
      switchAudioDevice,
    }),
    [state, isLoading, settingsLoading, openBroadcaster, closeBroadcaster, currentOrganization?.suspensions, setPortalTarget, switchVideoDevice, switchAudioDevice],
  );

  const isNavigationClose = closeReason === CLOSE_REASONS.NAVIGATION;
  const isBroadcasterLoading = isLoading || settingsLoading || !broadcasterCredentials;

  return (
    <BroadcasterContext.Provider value={value}>
      {children}
      {state.isOpen &&
        createPortal(
          <BaasBroadcaster
            broadcaster={broadcasterInstance}
            broadcasterBroadcasting={state.broadcasterState?.broadcasting}
            broadcasterVideoSurface={broadcasterVideoSurface}
            broadcasterIsRegistered={state.broadcasterState?.registered}
            broadcasterMuted={state.broadcasterState?.muted}
            broadcasterCameraOn={state.broadcasterState?.cameraOn}
            broadcasterHasInputAccess={state.broadcasterState?.inputAccess}
            onClose={closeBroadcaster}
            isInline={!!portalTarget}
            isLoading={isBroadcasterLoading}
            eventId={state.broadcasterProps?.eventId}
            publicBaseUrl={state.broadcasterProps?.publicBaseUrl}
            aspectRatio={aspectRatio}
            onAspectRatioChange={setResolution}
          />,
          portalTarget || document.body,
        )}
      <Modal
        overlayStyle={{ zIndex: 10 }}
        name="confirm-close-broadcaster-modal"
        isModalOpen={isCloseDialogOpen}
        onClose={handleCancelClose}
        title={isNavigationClose ? t('broadcaster.confirmNavigationCloseTitle') : t('broadcaster.confirmCloseTitle')}
        variant="confirmation"
        actions={
          <>
            <Button variant="regular" onClick={handleCancelClose}>
              {t('genericLabels.cancel')}
            </Button>
            <Button variant="primary" onClick={close}>
              {t('moderateShow.stopButtonLabel')}
            </Button>
          </>
        }
      >
        <div style={{ whiteSpace: 'pre-line' }}>
          {isNavigationClose ? t('broadcaster.confirmNavigationCloseMessage') : t('broadcaster.confirmCloseMessage')}
        </div>
      </Modal>
    </BroadcasterContext.Provider>
  );
};

export const useBroadcaster = () => useContext(BroadcasterContext);
